import React, { useEffect, useMemo, useRef, useState } from "react";
import ServerRequest from "../../../exports/ServerRequest";
import { GetCookie } from "../../../exports/CookieManagement";
import {
    Cross,
    Edit,
    Trash,
    UserSearch,
    Add,
    ArrowUp2,
    ArrowDown2,
    Tick,
    Search,
    User,
    Duplicate,
    Json,
    Calculator,
} from "../../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../../exports/ColorVars";
import Pulse from "../../Loader/Pulse";
import "../../../css/AdminManagement/CustomReportManagement.css";
import { Alert } from "../../Body/CustomChartComponents/CustomAlert/CustomAlert";

const warning_config = {
    Band: true,
    BandColor: "var(--main-blue)",
    BoxColor: "var(--white)",
    TextColor: "var(--text-color)",
    AutoClose: {
        Active: true,
        Line: true,
        LineColor: "var(--main-blue)",
        Time: 3,
    },
};

/* ------------------------------------------------------------------ *
 *  Field catalogue — copied from ReportTemplateDesigner so both stay
 *  in sync. Two levels: a group, and (for TCA) subgroups.
 *  Each entry is [dbFieldName, type, description].
 * ------------------------------------------------------------------ */
const FIELD_GROUPS = [
    {
        group: "Account & portfolio",
        fields: [
            ["Date", "string", "Trade date"],
            ["ClientID", "string", "Broking account code"],
            ["accountCode", "string", "Swiftfolios account code"],
            ["CPCode", "string", "Custodial participant code"],
            ["dp_id", "string", "Depository participant ID"],
        ],
    },
    {
        group: "Instrument",
        fields: [
            ["isin", "string", "Security ISIN"],
            ["stockCode", "string", "stock symbol"],
            ["Exchange", "string", "Trading exchange"],
            ["stockName", "string", "Security name"],
            ["expiry", "string", "Contract expiry date"],
            ["instrument_token", "string", "Instrument identifier token"],
            ["exchange_token", "string", "Exchange instrument token"],
            ["instrument_type", "string", "Instrument classification"],
            ["segment", "string", "Market segment"],
            ["strike", "number", "Option strike price"],
        ],
    },
    {
        group: "Order & execution",
        fields: [
            ["Quantity", "number", "Order quantity"],
            ["ExecutedQty", "number", "Executed quantity"],
            ["combined_qty", "number", "Combined order quantity"],
            ["Side", "string", "Buy or sell"],
            ["orderType", "string", "Order type"],
            ["limitPrice", "number", "Limit price"],
            ["trxnPrice", "number", "Transaction price"],
            ["Status", "string", "Order status"],
            ["trading_type", "string", "Trading type"],
        ],
    },
    {
        group: "Value & charges",
        fields: [
            ["grossValue", "number", "Gross transaction value"],
            ["Brokerage", "number", "Brokerage charge"],
            ["exchangeTransactionsCharges", "number", "Exchange transaction charges"],
            ["stampDuty", "number", "Stamp duty charge"],
            ["STT", "number", "Securities transaction tax"],
            ["SEBICharges", "number", "SEBI regulatory charges"],
            ["IGST", "number", "Integrated GST"],
            ["Net", "number", "Net transaction value"],
        ],
    },
    {
        group: "References",
        fields: [
            ["trxnRefNo", "string", "Transaction reference number"],
            ["brokerCode", "string", "Name of broker"],
            ["tradeOrderID", "string", "Trade order identifier"],
            ["userOrderID", "string", "User order identifier"],
            ["ClientOrderId", "string", "FIX / Client Order ID"],
            ["ExchangeOrderId", "string", "Exchange order identifier"],
            ["combined_id", "string", "Combined order identifier"],
        ],
    },
    {
        group: "TCA",
        subgroups: [
            { name: "Order", fields: [["newAt", "date", "Order acknowledgement timestamp"]] },
            {
                name: "Spread Cost",
                fields: [
                    ["spreadCostbps", "number", "Spread cost in bps"],
                    ["spreadCostamount", "number", "Spread cost amount"],
                    ["spreadCostspread", "number", "Bid-ask spread"],
                    ["spreadCosthalfSpread", "number", "Half bid-ask spread"],
                ],
            },
            {
                name: "Market Impact",
                fields: [
                    ["marketImpactbps", "number", "Market impact in bps"],
                    ["marketImpactamount", "number", "Market impact amount"],
                ],
            },
            {
                name: "Execution Stats",
                fields: [
                    ["executionStatsask", "number", "Ask price"],
                    ["executionStatsbid", "number", "Bid price"],
                    ["executionStatsside", "string", "Order side"],
                    ["executionStatsfillRate", "number", "Order fill percentage"],
                    ["executionStatsorderQty", "number", "Total order quantity"],
                    ["executionStatsexecutedQty", "number", "Executed order quantity"],
                    ["executionStatsunfilledQty", "number", "Unfilled order quantity"],
                    ["executionStatsarrivalPrice", "number", "Arrival market price"],
                    ["executionStatsdecisionPrice", "number", "Decision market price"],
                    ["executionStatspostTradePrice", "number", "Post-trade market price"],
                    ["executionStatsavgExecutionPrice", "number", "Average execution price"],
                ],
            },
            {
                name: "Implementation Shortfall — Total",
                fields: [
                    ["implementationShortfalltotalbps", "number", "Total shortfall in bps"],
                    ["implementationShortfalltotalamount", "number", "Total shortfall amount"],
                ],
            },
            {
                name: "Implementation Shortfall — Delay Cost",
                fields: [
                    ["implementationShortfalldelayCostbps", "number", "Delay cost in bps"],
                    ["implementationShortfalldelayCostamount", "number", "Delay cost amount"],
                ],
            },
            {
                name: "Implementation Shortfall — Execution Cost",
                fields: [
                    ["implementationShortfallexecutionCostbps", "number", "Execution cost in bps"],
                    ["implementationShortfallexecutionCostamount", "number", "Execution cost amount"],
                ],
            },
            {
                name: "Implementation Shortfall — Opportunity Cost",
                fields: [
                    ["implementationShortfallopportunityCostbps", "number", "Opportunity cost in bps"],
                    ["implementationShortfallopportunityCostamount", "number", "Opportunity cost amount"],
                ],
            },
            {
                name: "Latency",
                fields: [
                    ["latencyprocessing", "string", "Processing latency"],
                    ["latencytransmission", "string", "Transmission latency"],
                ],
            },
            {
                name: "Market Data",
                fields: [
                    ["marketDataask", "number", "Market ask price"],
                    ["marketDatabid", "number", "Market bid price"],
                ],
            },
            {
                name: "Timestamps",
                fields: [
                    ["createdAt", "date", "Order creation timestamp"],
                    ["transmitAt", "date", "Order transmission timestamp"],
                    ["completedAt", "date", "Order completion timestamp"],
                    ["pendingNewAt", "date", "Pending-new timestamp"],
                ],
            },
            {
                name: "Prices",
                fields: [
                    ["arrivalPrice", "number", "Arrival benchmark price"],
                    ["decisionPrice", "number", "Decision benchmark price"],
                    ["benchmarkPrice", "number", "Benchmark reference price"],
                ],
            },
        ],
    },
];

const ALL_FIELDS = FIELD_GROUPS.flatMap((g) =>
    g.subgroups
        ? g.subgroups.flatMap((s) =>
            s.fields.map(([name, type, desc]) => ({ name, type, desc, group: g.group, sub: s.name }))
        )
        : g.fields.map(([name, type, desc]) => ({ name, type, desc, group: g.group, sub: null }))
);
const FIELD_TYPE = Object.fromEntries(ALL_FIELDS.map((f) => [f.name, f.type]));
const FIELD_DESC = Object.fromEntries(ALL_FIELDS.map((f) => [f.name, f.desc]));
const NUMERIC_FIELDS = ALL_FIELDS.filter((f) => f.type === "number");

const uid = () => Math.random().toString(36).slice(2, 10);
const newTemplate = (name = "Untitled template") => ({ id: uid(), name, columns: [], emails: [] });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_REPORT_EMAILS = 3;

function exprToString(tokens) {
    return tokens
        .map((t) => t.v)
        .join(" ")
        .replace(/\(\s/g, "(")
        .replace(/\s\)/g, ")");
}

function validateExpr(tokens) {
    if (!tokens.length) return "Add at least one field or number.";
    let depth = 0;
    let expectOperand = true;
    for (const t of tokens) {
        if (t.v === "(") {
            if (!expectOperand) return "An operator is needed before an opening bracket.";
            depth++;
        } else if (t.v === ")") {
            if (expectOperand) return "A bracket was closed before an operand.";
            depth--;
            if (depth < 0) return "There is a closing bracket with no opening bracket.";
        } else if (t.k === "op") {
            if (expectOperand) return "Two operators in a row — add a field or number between them.";
            expectOperand = true;
        } else {
            if (!expectOperand) return "Two operands in a row — add an operator between them.";
            expectOperand = false;
        }
    }
    if (expectOperand) return "The formula ends on an operator.";
    if (depth !== 0) return "Close every bracket you open.";
    return null;
}

const CustomReportManagement = () => {
    const token = GetCookie("admin_token");

    const [loading, setLoading] = useState(false);
    const [allSuperUser, setAllSuperUser] = useState({});
    const [savedReports, setSavedReports] = useState([]);
    const [reportsLoaded, setReportsLoaded] = useState(false);
    const [isTemplateSaved, setIsTemplateSaved] = useState(true);
    const [hydrated, setHydrated] = useState(false);

    const [customerQuery, setCustomerQuery] = useState("");
    const [customerId, setCustomerId] = useState(null);

    const [store, setStore] = useState({});
    const [activeTemplateId, setActiveTemplateId] = useState(null);

    const [fieldQuery, setFieldQuery] = useState("");
    const [picked, setPicked] = useState(() => new Set());
    const [openGroups, setOpenGroups] = useState(() => new Set(["Account & portfolio"]));

    const [calcOpen, setCalcOpen] = useState(false);
    const [calcName, setCalcName] = useState("");
    const [calcFormula, setCalcFormula] = useState({
        mode: "common",      // common | bySide
        common: [],
        buy: [],
        sell: [],
        active: "common"     // common | buy | sell
    });
    const [calcLiteral, setCalcLiteral] = useState("");
    const [calcFieldQuery, setCalcFieldQuery] = useState("");
    const [editingCalcId, setEditingCalcId] = useState(null);

    const [renaming, setRenaming] = useState(false);
    const [showJson, setShowJson] = useState(false);
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const [emailInput, setEmailInput] = useState("");
    const [emailError, setEmailError] = useState("");

    const [reportHistory, setReportHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [generating, setGenerating] = useState(false);

    const activeTokens = calcFormula[calcFormula.active];
    const updateActiveTokens = (tokens) => {
        setCalcFormula(prev => ({
            ...prev,
            [prev.active]: tokens
        }));
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchSuperUser(), fetchReports()]);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    const fetchSuperUser = async () => {
        try {
            const res = await ServerRequest({
                url: `/admin/superuser/get?token=${token}`,
                method: "GET",
            });
            setAllSuperUser(res);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchReports = async () => {
        try {
            const res = await ServerRequest({
                url: `/backoffice/reportdata?back_token=${token}`,
                method: "GET",
            });
            setSavedReports(Array.isArray(res) ? res : res?.data || []);

        } catch (error) {
            console.error(error);
        } finally {
            setReportsLoaded(true);
        }
    };

    /* customers, sourced from the superuser API — each row is
       { superUser: {...}, brokers: [...] } */
    const customers = useMemo(() => {
        const list = allSuperUser?.data || [];
        return list
            .filter((c) => c.superUser)
            .map((c) => ({
                id: c.superUser.account_code,
                name: c.superUser.name || "Unnamed",
                accountCode: c.superUser.account_code || "-",
                email: c.superUser.email || "-",
                mobile: c.superUser.mobile || "-",
                status: c.superUser.status || "-",
                brokerCount: (c.brokers || []).length,
            }));
    }, [allSuperUser]);

    useEffect(() => {

        if (hydrated || !customers.length || !reportsLoaded) return;

        const grouped = {};
        (savedReports || []).forEach((r) => {
            const key = r.account_code;
            if (!key || !r.template_id || !Array.isArray(r.columns)) return;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push({
                id: r.template_id,
                name: r.name || "Report template",
                columns: r.columns.map((c) =>
                    c.kind === "calc"
                        ? { id: uid(), kind: "calc", label: c.label, expression: c.expression, tokens: c.tokens || [] }
                        : { id: uid(), kind: "field", source: c.field, label: c.label }
                ),
                // backend sends this as a JSON array already; the fallback
                // covers a driver that hands it back as a string.
                emails: Array.isArray(r.emails)
                    ? r.emails
                    : (() => {
                        try {
                            const parsed = JSON.parse(r.emails || "[]");
                            return Array.isArray(parsed) ? parsed : [];
                        } catch {
                            return [];
                        }
                    })(),
            });
        });
        setStore(grouped);
        setCustomerId((prev) => prev || customers[0]?.id || null);
        setHydrated(true);
    }, [customers, savedReports, hydrated]);

    const customer = customers.find((c) => c.id === customerId) || null;
    const templates = (customer && store[customer.id]) || [];
    const template = templates.find((t) => t.id === activeTemplateId) || templates[0] || null;

    useEffect(() => {
        setActiveTemplateId(templates[0]?.id ?? null);
        setPicked(new Set());
        closeCalcPanel();
        setShowJson(false);
        setEmailInput("");
        setEmailError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        if (!template) return;

        const saved = savedReports.some(
            (r) => r.template_id === template.id
        );

        setIsTemplateSaved(saved);

    }, [template?.id, savedReports]);

    /* report history, scoped to whichever template is currently open */
    useEffect(() => {
        if (!template?.id) {
            setReportHistory([]);
            return;
        }
        let cancelled = false;
        const loadHistory = async () => {
            setHistoryLoading(true);
            try {
                const res = await ServerRequest({
                    url: `/backoffice/reportdata/history/${template.id}?back_token=${token}`,
                    method: "GET",
                });
                const rows = Array.isArray(res) ? res : res?.data || [];
                if (!cancelled) setReportHistory(rows);
            } catch (error) {
                console.error(error);
                if (!cancelled) setReportHistory([]);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        };
        loadHistory();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.id]);

    const writeTemplates = (fn) =>
        setStore((s) => ({ ...s, [customer.id]: fn(s[customer.id] || []) }));

    const patchTemplate = (patch) =>
        writeTemplates((list) => list.map((t) => (t.id === template?.id ? { ...t, ...patch } : t)));

    const addTemplate = () => {
        if (!customer) return;
        const t = newTemplate(`Template ${(templates.length || 0) + 1}`);
        writeTemplates((list) => [...list, t]);
        setActiveTemplateId(t.id);
        setRenaming(true);
        setIsTemplateSaved(false);
    };

    const duplicateTemplate = () => {
        if (!template) return;
        const copy = {
            ...template,
            id: uid(),
            name: `${template.name} copy`,
            columns: template.columns.map((c) => ({ ...c, id: uid() })),
        };
        writeTemplates((list) => [...list, copy]);
        setActiveTemplateId(copy.id);
        setIsTemplateSaved(false);
    };

    const deleteTemplate = async () => {
        if (!template) return;
        const target = template;
        const remaining = templates.filter((t) => t.id !== target.id);
        writeTemplates(() => remaining);
        setActiveTemplateId(remaining[0]?.id ?? null);

        // if it was never saved, there's nothing to delete server-side
        const wasSaved = (savedReports || []).some((r) => r.template_id === target.id);
        if (!wasSaved) return;

        try {
            await ServerRequest({
                url: `/backoffice/reportdata/${target.id}?back_token=${token}`,
                method: "DELETE",
            });
        } catch (error) {
            console.error(error);
            // restore on failure so a deleted-on-screen-but-not-on-server
            // template doesn't just vanish silently
            writeTemplates((list) => [...list, target]);
            setActiveTemplateId(target.id);
        }
    };

    const addColumns = () => {
        if (!template || picked.size === 0) return;
        const existing = new Set(
            template.columns.filter((c) => c.kind === "field").map((c) => c.source)
        );
        const additions = [...picked]
            .filter((name) => !existing.has(name))
            .map((name) => ({ id: uid(), kind: "field", source: name, label: FIELD_DESC[name] }));
        patchTemplate({ columns: [...template.columns, ...additions] });
        setIsTemplateSaved(false);
        setPicked(new Set());
    };

    const closeCalcPanel = () => {
        setCalcOpen(false);
        setCalcName("");
        setCalcFormula({
            mode: "common",
            common: [],
            buy: [],
            sell: [],
            active: "common"
        });
        setCalcLiteral("");
        setCalcFieldQuery("");
        setEditingCalcId(null);
    };

    const startEditCalc = (c) => {
        setCalcName(c.label);
        setCalcFormula({
            mode: c.mode || "common",
            common: c.commonTokens || c.tokens || [],
            buy: c.buyTokens || [],
            sell: c.sellTokens || [],
            active: c.mode === "bySide" ? "buy" : "common"
        });
        setCalcFieldQuery("");
        setCalcLiteral("");
        setEditingCalcId(c.id);
        setCalcOpen(true);
    };

    const addCalculated = () => {
        if (validateExpr(activeTokens) || !template) return;
        const built = {
            kind: "calc",
            label: calcName.trim() || "Calculated column",

            mode: calcFormula.mode,

            commonTokens: calcFormula.common,
            buyTokens: calcFormula.buy,
            sellTokens: calcFormula.sell,

            commonExpression: exprToString(calcFormula.common),
            buyExpression: exprToString(calcFormula.buy),
            sellExpression: exprToString(calcFormula.sell),
        };
        if (editingCalcId) {
            updateColumn(editingCalcId, built);
        } else {
            patchTemplate({ columns: [...template.columns, { id: uid(), ...built }] });
            setIsTemplateSaved(false);
        }
        closeCalcPanel();
    };

    const addEmail = () => {
        if (!template) return;
        const value = emailInput.trim().toLowerCase();
        const existing = template.emails || [];
        if (!value) return;
        if (!EMAIL_RE.test(value)) {
            setEmailError("Enter a valid email address.");
            return;
        }
        if (existing.includes(value)) {
            setEmailError("That email is already added.");
            return;
        }
        if (existing.length >= MAX_REPORT_EMAILS) {
            setEmailError(`You can save up to ${MAX_REPORT_EMAILS} recipients.`);
            return;
        }
        patchTemplate({ emails: [...existing, value] });
        setIsTemplateSaved(false);
        setEmailInput("");
        setEmailError("");
    };

    const removeEmail = (index) => {
        const existing = template?.emails || [];
        patchTemplate({ emails: existing.filter((_, i) => i !== index) });
        setIsTemplateSaved(false);
        setEmailError("");
    };

    const moveColumn = (index, delta) => {
        const next = [...template.columns];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        patchTemplate({ columns: next });
        setIsTemplateSaved(false);
    };

    const updateColumn = (id, patch) => {
        patchTemplate({ columns: template.columns.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
        setIsTemplateSaved(false);
    }

    const removeColumn = (id) => {
        patchTemplate({ columns: template.columns.filter((c) => c.id !== id) });
        setIsTemplateSaved(false);
    }

    const visibleCustomers = useMemo(() => {
        const q = customerQuery.trim().toLowerCase();
        if (!q) return customers;
        return customers.filter(
            (c) => c.name.toLowerCase().includes(q) || c.accountCode.toLowerCase().includes(q)
        );
    }, [customers, customerQuery]);

    const visibleGroups = useMemo(() => {
        const q = fieldQuery.trim().toLowerCase();
        const keep = ([name, , desc]) =>
            name.toLowerCase().includes(q) || (desc || "").toLowerCase().includes(q);
        return FIELD_GROUPS.map((g) =>
            g.subgroups
                ? {
                    group: g.group,
                    subgroups: g.subgroups
                        .map((s) => ({ name: s.name, fields: s.fields.filter(keep) }))
                        .filter((s) => s.fields.length),
                }
                : { group: g.group, fields: g.fields.filter(keep) }
        ).filter((g) => (g.subgroups ? g.subgroups.length : g.fields.length));
    }, [fieldQuery]);

    const calcFieldMatches = useMemo(() => {
        const q = calcFieldQuery.trim().toLowerCase();
        if (!q) return NUMERIC_FIELDS;
        return NUMERIC_FIELDS.filter(
            (f) => f.name.toLowerCase().includes(q) || (f.desc || "").toLowerCase().includes(q)
        );
    }, [calcFieldQuery]);

    const inTemplate = useMemo(
        () => new Set((template?.columns || []).filter((c) => c.kind === "field").map((c) => c.source)),
        [template]
    );

    const calcError = validateExpr(activeTokens);

    const payload = template && customer
        ? {
            customer: { id: customer.id, name: customer.name, accountCode: customer.accountCode },
            template: {
                template_id: template.id,
                name: template.name,
                emails: template.emails || [],
                columns: template.columns.map((c, i) => ({
                    order: i + 1,
                    label: c.label,
                    kind: c.kind,
                    ...(c.kind === "field"
                        ? { field: c.source, dataType: FIELD_TYPE[c.source] }
                        : {
                            mode: c.mode,

                            commonTokens: c.commonTokens || [],
                            buyTokens: c.buyTokens || [],
                            sellTokens: c.sellTokens || [],

                            commonExpression: c.commonExpression || "",
                            buyExpression: c.buyExpression || "",
                            sellExpression: c.sellExpression || "",

                            dataType: "number"
                        }),
                })),
            },
        }
        : null;

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            /* clipboard unavailable */
        }
    };

    /* template.id is the client-generated template_id (see newTemplate),
       sent on every save so the backend can upsert on that key instead of
       always inserting a new row. TODO: confirm ServerRequest's body param
       name (data / body / payload) matches your helper. */
    const saveTemplate = async () => {
        if (!template || !customer) return;
        if (!template.name.trim()) {
            setRenaming(true);
            return;
        }
        setSaving(true);
        try {
            await ServerRequest({
                url: `/backoffice/reportdata?back_token=${token}`,
                method: "POST",
                data: {
                    template_id: template.id,
                    account_code: customer.accountCode,
                    name: template.name,
                    columns: payload.template.columns,
                    emails: template.emails || [],
                },
            });
            setSavedAt(new Date());
            setIsTemplateSaved(true);
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const generateReport = async () => {

        if (!template || !customer) return;

        if (new Date(fromDate) > new Date(toDate)) {
            Alert({
                TitleText: "Warning",
                Message: "From Date cannot be later than To Date.",
                ...warning_config
            });

            return;
        }

        try {

            setGenerating(true);

            const res = await ServerRequest({
                url: `/backoffice/generate/excelreport?back_token=${token}`,
                method: "POST",
                data: {
                    account_code: customer.accountCode,
                    template_id: template.id,
                    from_date: fromDate,
                    to_date: toDate
                },
                headers: {
                    "x-api-key": process.env.REACT_APP_REPORT_API_KEY
                }

            });

            if (res.no_data) {
                Alert({
                    TitleText: "Warning",
                    Message: "No data found for the selected date range.",
                    ...warning_config,
                });
                return;
            }

            if (res.success) {

                window.open(res.report_url, "_blank");

                // refresh history
                const history = await ServerRequest({
                    url: `/backoffice/reportdata/history/${template.id}?back_token=${token}`,
                    method: "GET",
                });

                setReportHistory(Array.isArray(history) ? history : history.data || []);
            }

        } finally {
            setGenerating(false);
        }
    };

    const toggleGroup = (name) =>
        setOpenGroups((s) => {
            const n = new Set(s);
            n.has(name) ? n.delete(name) : n.add(name);
            return n;
        });

    const togglePick = (name) =>
        setPicked((s) => {
            const n = new Set(s);
            n.has(name) ? n.delete(name) : n.add(name);
            return n;
        });

    const FieldRow = ({ name, type, desc, indent }) => {
        const used = inTemplate.has(name);
        return (
            <label className={`report-designer-field-row ${indent ? "indent" : ""} ${used ? "used" : ""}`}>
                <input
                    type="checkbox"
                    disabled={used}
                    checked={picked.has(name)}
                    onChange={() => togglePick(name)}
                />
                <span className="report-designer-field-row-text">
                    <span className="report-designer-field-row-desc">{desc}</span>
                    <span className="report-designer-field-row-name">{name}</span>
                </span>
                <span className={`report-designer-badge ${type}`}>{type}</span>
                {used && <Tick size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />}
            </label>
        );
    };

    return !loading ? (
        <div className="swift-account-main open report-designer-page">
            <div className="report-designer-wrapper">
                <div className="report-designer-left">
                    {/* ---------------- column 1: customer ---------------- */}
                    <section className="report-designer-column" >
                        <div className="report-designer-column-head">
                            <h2>Customer</h2>
                            <span className="report-designer-muted">{visibleCustomers.length}</span>
                        </div>
                        <div className="report-designer-search-box">
                            <input
                                placeholder="Search Customer"
                                value={customerQuery}
                                onChange={(e) => setCustomerQuery(e.target.value)}
                            />
                            <div className="filter-icon">
                                <UserSearch size={18} color={COLOR_VARS["SWIFT_COLOR4"]} />
                            </div>
                        </div>
                        <ul className="report-designer-customer-list">
                            {visibleCustomers.map((c) => {
                                const active = c.id === customerId;
                                const count = (store[c.id] || []).length;
                                return (
                                    <li key={c.id}>
                                        <button
                                            className={`report-designer-customer-item ${active ? "active" : ""}`}
                                            onClick={() => setCustomerId(c.id)}
                                        >
                                            <div className="report-designer-customer-item-row">
                                                <span>{c.name}</span>
                                                {count > 0 && <span className="report-designer-count-pill">{count}</span>}
                                            </div>
                                            <div className="report-designer-customer-item-code"><User size={14} color={COLOR_VARS["SWIFT_COLOR4"]} /> {c.accountCode}</div>
                                        </button>
                                    </li>
                                );
                            })}
                            {visibleCustomers.length === 0 && (
                                <li className="report-designer-empty">No customer matches that search.</li>
                            )}
                        </ul>
                    </section>

                    {/* ---------------- column 2: fields ---------------- */}
                    <section className="report-designer-column">
                        <div className="report-designer-column-head">
                            <h2>Available Fields</h2>
                            <button
                                className={`report-designer-chip-btn ${calcOpen ? "active" : ""}`}
                                onClick={() => (calcOpen ? closeCalcPanel() : setCalcOpen(true))}
                            >
                                <Calculator size={14} color={COLOR_VARS.SWIFT_COLOR4} />
                                {calcOpen ? "Close" : "Calculated Field"}
                            </button>
                        </div>

                        <div className="report-designer-search-box no-border">
                            <div className="filter-icon">
                                <Search size={18} color={COLOR_VARS["SWIFT_COLOR4"]} />
                            </div>
                            <input
                                placeholder="Filter fields"
                                value={fieldQuery}
                                onChange={(e) => setFieldQuery(e.target.value)}
                            />
                        </div>

                        {calcOpen && (
                            <div className="report-designer-calc-panel">
                                <p className="report-designer-calc-title">
                                    {editingCalcId ? "Edit calculated field" : "Build a calculated field"}
                                </p>
                                <p className="report-designer-calc-sub">
                                    Combine numeric fields with + − × ÷ and brackets. As many operands as you need.
                                </p>

                                <input
                                    className="report-designer-input"
                                    placeholder="Column name, e.g. Net after charges"
                                    value={calcName}
                                    onChange={(e) => setCalcName(e.target.value)}
                                />
                                <div className="formula-header">
                                    <div className="formula-mode">

                                        <label>
                                            <input
                                                type="radio"
                                                checked={calcFormula.mode === "common"}
                                                onChange={() =>
                                                    setCalcFormula({
                                                        ...calcFormula,
                                                        mode: "common",
                                                        active: "common"
                                                    })
                                                }
                                            />

                                            Common Formula

                                        </label>

                                        <label>
                                            <input
                                                type="radio"
                                                checked={calcFormula.mode === "bySide"}
                                                onChange={() =>
                                                    setCalcFormula({
                                                        ...calcFormula,
                                                        mode: "bySide",
                                                        active: "buy"
                                                    })
                                                }
                                            />

                                            Buy / Sell Formula

                                        </label>

                                    </div>

                                    <div className="side_tabs">

                                        <button
                                            className={calcFormula.active === "buy" ? "report-designer-chip-btn active" : "report-designer-chip-btn"}
                                            onClick={() =>
                                                setCalcFormula({
                                                    ...calcFormula,
                                                    active: "buy"
                                                })
                                            }
                                            disabled={calcFormula.mode === 'common'}
                                        >

                                            BUY

                                        </button>

                                        <button
                                            className={calcFormula.active === "sell" ? "report-designer-chip-btn active" : "report-designer-chip-btn"}
                                            onClick={() =>
                                                setCalcFormula({
                                                    ...calcFormula,
                                                    active: "sell"
                                                })
                                            }
                                            disabled={calcFormula.mode === 'common'}
                                        >

                                            SELL

                                        </button>

                                    </div>
                                </div>

                                <div className="report-designer-token-box">
                                    {activeTokens.length === 0 && (
                                        <span className="report-designer-muted">
                                            Pick a field, an operator, then the next operand.
                                        </span>
                                    )}
                                    {activeTokens.map((t, i) => (
                                        <span key={i} className={`report-designer-token ${t.k}`}>
                                            {t.v}
                                        </span>
                                    ))}
                                </div>

                                <div className="report-designer-op-row">
                                    {[
                                        ["+", "+"],
                                        ["-", "−"],
                                        ["*", "×"],
                                        ["/", "÷"],
                                    ].map(([op, glyph]) => (
                                        <button
                                            key={op}
                                            className="report-designer-chip-btn square"
                                            onClick={() => updateActiveTokens([
                                                ...activeTokens, { k: "op", v: op }])}
                                        >
                                            {glyph}
                                        </button>
                                    ))}
                                    {["(", ")"].map((p) => (
                                        <button
                                            key={p}
                                            className="report-designer-chip-btn square"
                                            onClick={() => updateActiveTokens([
                                                ...activeTokens, { k: "paren", v: p }])}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        className="report-designer-chip-btn"
                                        disabled={!activeTokens.length}
                                        onClick={() => updateActiveTokens(activeTokens.slice(0, -1))}
                                    >
                                        Undo
                                    </button>
                                    <button
                                        className="report-designer-chip-btn"
                                        disabled={!activeTokens.length}
                                        onClick={() => updateActiveTokens([])}
                                    >
                                        Clear
                                    </button>
                                </div>

                                <div className="report-designer-calc-row">
                                    <input
                                        className="report-designer-input"
                                        placeholder="Search a numeric field"
                                        value={calcFieldQuery}
                                        onChange={(e) => setCalcFieldQuery(e.target.value)}
                                    />
                                    <input
                                        className="report-designer-input small"
                                        placeholder="123"
                                        inputMode="decimal"
                                        value={calcLiteral}
                                        onChange={(e) => setCalcLiteral(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && calcLiteral.trim()) {
                                                updateActiveTokens([
                                                    ...activeTokens, { k: "num", v: calcLiteral.trim() }]);
                                                setCalcLiteral("");
                                            }
                                        }}
                                    />
                                    <button
                                        className="report-designer-chip-btn"
                                        disabled={!calcLiteral.trim()}
                                        onClick={() => {
                                            updateActiveTokens([
                                                ...activeTokens, { k: "num", v: calcLiteral.trim() }]);
                                            setCalcLiteral("");
                                        }}
                                    >
                                        Add
                                    </button>
                                </div>

                                <div className="report-designer-calc-meta">
                                    <span className="report-designer-muted">
                                        {calcFieldMatches.length} numeric {calcFieldMatches.length === 1 ? "field" : "fields"}
                                    </span>
                                    {calcFieldQuery && (
                                        <button className="report-designer-link-btn" onClick={() => setCalcFieldQuery("")}>
                                            Clear search
                                        </button>
                                    )}
                                </div>

                                <div className="report-designer-calc-field-list">
                                    {calcFieldMatches.map((f) => (
                                        <button
                                            key={f.name}
                                            className="report-designer-calc-field-row"
                                            onClick={() => updateActiveTokens([
                                                ...activeTokens, { k: "field", v: f.name }])}
                                        >
                                            <span className="report-designer-field-row-text">
                                                <span className="report-designer-field-row-desc">{f.desc}</span>
                                                <span className="report-designer-field-row-name">{f.name}</span>
                                            </span>
                                            <span className="report-designer-muted">{f.sub || f.group}</span>
                                        </button>
                                    ))}
                                    {calcFieldMatches.length === 0 && (
                                        <p className="report-designer-empty">No numeric field matches.</p>
                                    )}
                                </div>

                                {activeTokens.length > 0 && calcError && (
                                    <p className="report-designer-warning">{calcError}</p>
                                )}

                                {!calcError && (<div className="report-designer-calc-footer">
                                    <code className="report-designer-expr">
                                        {activeTokens.length ? exprToString(activeTokens) : "—"}
                                    </code>
                                    <button
                                        className="report-designer-primary-btn"
                                        disabled={!!calcError || !template}
                                        onClick={addCalculated}
                                    >
                                        {editingCalcId ? "Save changes" : "Add"}
                                    </button>
                                </div>)}
                            </div>
                        )}

                        <div className="report-designer-field-groups">
                            {visibleGroups.map((g) => {
                                const open = openGroups.has(g.group) || fieldQuery.trim().length > 0;
                                const count = g.subgroups
                                    ? g.subgroups.reduce((n, s) => n + s.fields.length, 0)
                                    : g.fields.length;
                                return (
                                    <div key={g.group} className={`report-designer-field-group ${open && 'open'}`}>
                                        <div className={`report-designer-field-group-head ${open && 'open'}`} onClick={() => toggleGroup(g.group)}>
                                            <span>{g.group}</span>
                                            <span className="report-designer-group-meta">
                                                {count}
                                                {open ? (
                                                    <ArrowUp2 size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                ) : (
                                                    <ArrowDown2 size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                )}
                                            </span>
                                        </div>

                                        {open &&
                                            (g.subgroups
                                                ? g.subgroups.map((s) => (
                                                    <div key={s.name} className="report-designer-subgroup">
                                                        <p className="report-designer-subgroup-title">{s.name}</p>
                                                        {s.fields.map(([name, type, desc]) => (
                                                            <FieldRow key={name} name={name} type={type} desc={desc} indent />
                                                        ))}
                                                    </div>
                                                ))
                                                : g.fields.map(([name, type, desc]) => (
                                                    <FieldRow key={name} name={name} type={type} desc={desc} />
                                                )))}
                                    </div>
                                );
                            })}
                            {visibleGroups.length === 0 && (
                                <p className="report-designer-empty">No field name or description contains “{fieldQuery}”.</p>
                            )}
                        </div>

                        <div className="report-designer-column-foot">
                            <span className="report-designer-muted">
                                {picked.size ? `${picked.size} selected` : "Tick the fields you need"}
                            </span>
                            <button className="report-designer-primary-btn" disabled={!picked.size || !template} onClick={addColumns}>
                                Add to Template
                            </button>
                        </div>
                    </section>



                    {/* ---------------- column 3: template ---------------- */}
                    <section className="report-designer-column report-designer-right">
                        <div className="report-designer-column-head">
                            <div>
                                <h2>Template</h2>
                                {customer && (
                                    <p className="report-designer-muted small">
                                        {customer.name}
                                    </p>
                                )}
                            </div>
                            <div className="report-designer-head-actions">
                                <button className="report-designer-chip-btn" onClick={addTemplate} disabled={!customer}>
                                    <Add size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                    New
                                </button>
                                <button className="report-designer-chip-btn" onClick={duplicateTemplate} disabled={!template}>
                                    <Duplicate size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                    Duplicate
                                </button>
                                <button className="report-designer-chip-btn" onClick={() => setShowJson((v) => !v)} disabled={!template}>
                                    <Json size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                    {showJson ? "Columns" : "JSON"}
                                </button>
                            </div>
                        </div>

                        {!customer ? (
                            <div className="report-designer-column-empty">
                                <p>Select a customer to start a report template.</p>
                            </div>
                        ) : !template ? (
                            <div className="report-designer-column-empty">
                                <p>{customer.name} has no template yet.</p>
                                <button className="report-designer-primary-btn" onClick={addTemplate}>
                                    <Add size={16} color="#fff" />
                                    Create the first template
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="report-designer-template-head">
                                    <div className="report-designer-template-select-row">
                                        <select
                                            className="report-designer-input"
                                            value={template.id}
                                            onChange={(e) => setActiveTemplateId(e.target.value)}
                                        >
                                            {templates.map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} · {t.columns.length} columns
                                                </option>
                                            ))}
                                        </select>
                                        <button className="report-designer-icon-btn" onClick={() => setRenaming((v) => !v)} title="Rename">
                                            <Edit size={16} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                        </button>
                                        <button className="report-designer-icon-btn" onClick={deleteTemplate} title="Delete template">
                                            <Trash size={16} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                        </button>
                                    </div>
                                    {renaming && (
                                        <input
                                            autoFocus
                                            className="report-designer-input"
                                            value={template.name}
                                            onChange={(e) => {
                                                patchTemplate({ name: e.target.value })
                                                setIsTemplateSaved(false);
                                            }}
                                            onBlur={() => setRenaming(false)}
                                            onKeyDown={(e) => e.key === "Enter" && setRenaming(false)}
                                        />
                                    )}
                                </div>

                                <div className="report-designer-email-section">

                                    <div className="report-designer-token-box">
                                        {(template.emails || []).map((email, i) => (
                                            <span key={email} className="report-designer-token email">
                                                {email}
                                                <button
                                                    type="button"
                                                    className="report-designer-token-remove"
                                                    onClick={() => removeEmail(i)}
                                                    aria-label={`Remove ${email}`}
                                                    title="Remove"
                                                >
                                                    <Cross size={13} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>

                                    <div className="report-designer-calc-row">
                                        <input
                                            className="report-designer-input"
                                            placeholder="name@example.com"
                                            value={emailInput}
                                            disabled={(template.emails || []).length >= MAX_REPORT_EMAILS}
                                            onChange={(e) => {
                                                setEmailInput(e.target.value);
                                                if (emailError) setEmailError("");
                                            }}
                                            onKeyDown={(e) => e.key === "Enter" && addEmail()}
                                        />
                                        <button
                                            className="report-designer-chip-btn"
                                            onClick={addEmail}
                                            disabled={!emailInput.trim() || (template.emails || []).length >= MAX_REPORT_EMAILS}
                                        >
                                            Add
                                        </button>
                                        <span className="report-designer-muted">
                                            {(template.emails || []).length}/{MAX_REPORT_EMAILS}
                                        </span>
                                    </div>
                                    {emailError && <p className="report-designer-warning">{emailError}</p>}
                                </div>

                                {showJson ? (
                                    <div className="report-designer-json-view">
                                        <pre>{JSON.stringify(payload, null, 2)}</pre>
                                        <div className="report-designer-json-foot">
                                            <button className="report-designer-primary-btn" onClick={copyJson}>
                                                {copied ? <Tick size={16} color="#fff" /> : null}
                                                {copied ? "Copied" : "Copy JSON"}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="report-designer-columns-list">
                                            {template.columns.length === 0 && (
                                                <p className="report-designer-empty bordered">
                                                    Tick fields in the middle column, or build a calculated field, to start the layout.
                                                </p>
                                            )}
                                            {template.columns.map((c, i) => {
                                                const type = c.kind === "calc" ? "number" : FIELD_TYPE[c.source] || "string";
                                                return (
                                                    <div key={c.id} className="report-designer-column-item">
                                                        <div className="report-designer-column-item-order">
                                                            <button
                                                                className="report-designer-icon-btn"
                                                                onClick={() => moveColumn(i, -1)}
                                                                disabled={i === 0}
                                                            >
                                                                <ArrowUp2 size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                            </button>
                                                            <span className="report-designer-mono">{i + 1}</span>
                                                            <button
                                                                className="report-designer-icon-btn"
                                                                onClick={() => moveColumn(i, 1)}
                                                                disabled={i === template.columns.length - 1}
                                                            >
                                                                <ArrowDown2 size={14} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                            </button>
                                                        </div>
                                                        <div className="report-designer-column-item-body">
                                                            <div className="report-designer-column-item-row">
                                                                <input
                                                                    className="report-designer-input"
                                                                    value={c.label}
                                                                    onChange={(e) => updateColumn(c.id, { label: e.target.value })}
                                                                    placeholder="Column heading"
                                                                />
                                                                {c.kind === "calc" && (
                                                                    <button
                                                                        className="report-designer-icon-btn"
                                                                        onClick={() => startEditCalc(c)}
                                                                        title="Edit formula"
                                                                    >
                                                                        <Edit size={16} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                                    </button>
                                                                )}
                                                                <button className="report-designer-icon-btn" onClick={() => removeColumn(c.id)}>
                                                                    <Cross size={16} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                                </button>
                                                            </div>
                                                            <div className="report-designer-column-item-meta">
                                                                {c.kind === "calc" ? (
                                                                    <span className="report-designer-badge calc">calculated</span>
                                                                ) : (
                                                                    <span className={`report-designer-badge ${type}`}>{type}</span>
                                                                )}
                                                                <code className="report-designer-mono truncate">
                                                                    {c.kind === "calc" ? c.expression : c.source}
                                                                </code>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {template.columns.length > 0 && (
                                            <div className="report-designer-preview">
                                                <p className="report-designer-preview-title">Header Preview</p>
                                                <div className="report-designer-preview-table-wrap">
                                                    <table className="report-designer-preview-table">
                                                        <thead>
                                                            <tr>
                                                                {template.columns.map((c) => (
                                                                    <th key={c.id}>{c.label}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                {template.columns.map((c) => (
                                                                    <td key={c.id}>····</td>
                                                                ))}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        <div className="report-designer-column-foot">
                                            <span className="report-designer-muted">
                                                {template.columns.length} columns
                                                {savedAt &&
                                                    ` · saved ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                                            </span>
                                            <button
                                                className="report-designer-primary-btn"
                                                onClick={saveTemplate}
                                                disabled={!template.columns.length || saving}
                                            >
                                                {saving ? "Saving…" : "Save Template"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </section>
                </div>
                {/* ---------------- column 4: report history ---------------- */}
                <section className="report-designer-column report-designer-history">
                    <div className="report-generate-card">

                        <div className="report-generate-header">
                            <div>
                                <h3>Generate Report</h3>
                                <p>Generate an Excel report for the selected template.</p>
                            </div>
                        </div>

                        <div className="report-generate-dates">

                            <div>
                                <label>From Date</label>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>

                            <div>
                                <label>To Date</label>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>

                        </div>

                        {!isTemplateSaved && (
                            <div className="report-generate-warning">
                                Save the template before generating the report.
                            </div>
                        )}

                        <button
                            className="report-designer-primary-btn"
                            disabled={!isTemplateSaved || generating}
                            onClick={generateReport}
                        >
                            {generating
                                ? "Generating Report..."
                                : "Generate & Download"}
                        </button>

                    </div>
                    <div className="report-designer-column-head">
                        <h2>Report History</h2>
                        {template && <span className="report-designer-muted">{reportHistory.length}</span>}
                    </div>

                    {!customer ? (
                        <div className="report-designer-column-empty">
                            <p>Select a customer to see reports generated for them.</p>
                        </div>
                    ) : !template ? (
                        <div className="report-designer-column-empty">
                            <p>Select a template to see its report history.</p>
                        </div>
                    ) : historyLoading ? (
                        <div className="report-designer-column-empty">
                            <p>Loading history…</p>
                        </div>
                    ) : reportHistory.length === 0 ? (
                        <div className="report-designer-column-empty">
                            <p>No reports have been generated for “{template.name}” yet.</p>
                        </div>
                    ) : (
                        <ul className="report-designer-history-list">
                            {reportHistory.map((h, i) => (
                                <li key={h.id || `${h.report_url}-${i}`} className="report-designer-history-item">
                                    <div className="report-designer-history-item-text">
                                        <span className="report-designer-history-item-date">
                                            {h.report_date
                                                ? new Date(h.report_date).toLocaleDateString(undefined, {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })
                                                : "—"}
                                        </span>
                                        <span className="report-designer-mono truncate">{template.name}</span>
                                    </div>
                                    <a
                                        className="report-designer-link-btn"
                                        href={h.report_url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Download
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}

                    {template && (
                        <div className="report-designer-column-foot">
                            <span className="report-designer-muted">
                                {reportHistory.length} {reportHistory.length === 1 ? "report" : "reports"} generated
                            </span>
                        </div>
                    )}
                </section>
            </div>
        </div>
    ) : (
        <div className="admin-body-loader">
            <Pulse />
        </div>
    );
};

export default CustomReportManagement;